import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { authService } from '../services/auth'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    checkAuthStatus()
  }, [])

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setIsLoading(false)
        return
      }

      // Configurer le token AVANT de faire la requête
      authService.setAuthToken(token)

      // Vérifier la validité du token
      const userData = await authService.getMe()
      setUser(userData)
    } catch (error) {
      // Token invalide ou expiré
      localStorage.removeItem('token')
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (credentials) => {
    try {
      setIsLoading(true)
      const response = await authService.login(credentials)

      if (response.requireTwoFactor) {
        return { requireTwoFactor: true }
      }

      const { accessToken, user: userData } = response

      localStorage.setItem('token', accessToken)
      authService.setAuthToken(accessToken)
      setUser(userData)

      toast.success('Connexion réussie !')
      navigate('/dashboard')

      return { success: true }
    } catch (error) {
      toast.error(error.message || 'Erreur de connexion')
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const loginWithTwoFactor = async (credentials, twoFactorCode) => {
    try {
      setIsLoading(true)
      const response = await authService.login({
        ...credentials,
        twoFactorCode
      })

      const { accessToken, user: userData } = response

      localStorage.setItem('token', accessToken)
      authService.setAuthToken(accessToken)
      setUser(userData)

      toast.success('Connexion réussie !')
      navigate('/dashboard')

      return { success: true }
    } catch (error) {
      toast.error(error.message || 'Code invalide')
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (userData) => {
    try {
      setIsLoading(true)
      await authService.register(userData)
      toast.success('Compte créé avec succès ! Vous pouvez maintenant vous connecter.')
      navigate('/login')
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création du compte')
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error)
    } finally {
      localStorage.removeItem('token')
      authService.setAuthToken(null)
      setUser(null)
      toast.success('Déconnexion réussie')
      navigate('/login')
    }
  }

  const setupTwoFactor = async () => {
    try {
      const response = await authService.setupTwoFactor()
      return response
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la configuration 2FA')
      throw error
    }
  }

  const verifyTwoFactor = async (token) => {
    try {
      await authService.verifyTwoFactor(token)
      // Mettre à jour les données utilisateur
      const userData = await authService.getMe()
      setUser(userData)
      toast.success('Double authentification activée !')
    } catch (error) {
      toast.error(error.message || 'Code invalide')
      throw error
    }
  }

  const disableTwoFactor = async (password) => {
    try {
      await authService.disableTwoFactor(password)
      // Mettre à jour les données utilisateur
      const userData = await authService.getMe()
      setUser(userData)
      toast.success('Double authentification désactivée')
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la désactivation')
      throw error
    }
  }

  const hasRole = (roles) => {
    if (!user) return false
    const userRoles = Array.isArray(roles) ? roles : [roles]
    return userRoles.includes(user.role)
  }

  const hasPermission = (permission) => {
    if (!user) return false
    if (user.role === 'super_admin' || user.role === 'responsable') return true

    // Logique de permissions spécifiques à implémenter
    return false
  }

  const value = useMemo(() => ({
    user,
    isLoading,
    login,
    loginWithTwoFactor,
    register,
    logout,
    setupTwoFactor,
    verifyTwoFactor,
    disableTwoFactor,
    hasRole,
    hasPermission,
    isAuthenticated: !!user
  }), [user, isLoading])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}